/**
 * @file Static runtime config — device categories, status enums, domain regex.
 *
 * Admin allowlist lives in the /admins Firestore collection (one doc per
 * admin, keyed by lowercased email). The front-end subscribes to the
 * caller's own /admins row in auth.js; Cloud Functions check via
 * isAdminEmail() in functions/lib/admin-db.js; firestore.rules' isAdmin()
 * is an exists() lookup. One source of truth.
 */

/** Domain regex matching the firestore.rules `isStrv()` check. */
export const STRV_DOMAIN_RE = /^[^@]+@strv\.com$/;

/**
 * Flexi web UI base for deep links from the data-quality panel
 * ("Open in Flexi ↗"). Path appended is `/majetek/<kod>`.
 */
export const FLEXI_WEB_BASE = 'https://strv.flexibee.eu/flexi/strv_s_r_o_1';

export function flexiWebUrl(flexiKod) {
  return `${FLEXI_WEB_BASE}/majetek/${encodeURIComponent(flexiKod || '')}`;
}

/** Flexi web UI — top-level majetek list. Used when we want to point an
 *  admin at "go add this to Abra" without a specific record to deep-link
 *  to. They land on the list page and hit Add. */
export function flexiMajetekListUrl() {
  return `${FLEXI_WEB_BASE}/majetek/`;
}

/** QA testing-devices sheet — the same one syncTestingDevices reads. */
export const TESTING_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1r_Qp-oQo5voRVleTmQHxcf5fxL_P-Wb9TNkCYGqij2k/edit';

/** Meraki dashboard root. We don't construct per-device deep links because
 *  the URL needs a network + region prefix we don't have on the client.
 *  Admins land on the dashboard, search by SN, and edit from there. */
export const MERAKI_DASHBOARD_URL = 'https://dashboard.meraki.com/';

/** Categories of items Gear tracks across on/offboarding + the devices pool. */
export const CATEGORIES = Object.freeze({
  laptop:        { label: 'Laptop',         tracksSerial: true,  resaleFormula: 'laptop' },
  phone:         { label: 'Phone',          tracksSerial: true,  resaleFormula: 'phone'  },
  keyboard:      { label: 'Keyboard',       tracksSerial: false },
  mouse:         { label: 'Mouse',          tracksSerial: false },
  ext_screen:    { label: 'External screen', tracksSerial: false },
  charger:       { label: 'Extra charger',  tracksSerial: false },
  phone_tariff:  { label: 'Phone tariff',   tracksSerial: false },
  access_card:   { label: 'Access card',    tracksSerial: false },
  ai_tool:       { label: 'AI tooling',     tracksSerial: false },
  multisport:    { label: 'Multisport card', tracksSerial: false },
  notes:         { label: 'Notes',          tracksSerial: false },
  other:         { label: 'Other',          tracksSerial: false },
});

/** Phone tariff sub-options the joining employee can pick on the form. */
export const TARIFF_SUBCATEGORIES = Object.freeze({
  new:      'New number (new e-SIM)',
  transfer: 'Transfer existing number to STRV',
  none:     "Doesn't want one",
});

/** Onboarding per-item status state machine. */
export const ONBOARDING_STATUS = Object.freeze({
  requested: { label: 'Requested',  badge: 'idle'     },
  ordered:   { label: 'Ordered',    badge: 'warning'  },
  prepared:  { label: 'Prepared',   badge: 'ready'    },
  handed_over: { label: 'Handed over', badge: 'success'  },
  cancelled: { label: 'Cancelled',  badge: 'error'    },
});

/** Offboarding per-item decision state machine. The first set applies to
 *  physical devices (laptops, phones, peripherals); the second set is the
 *  phone-tariff branch and never crosses with device states.
 *
 *  Devices:
 *    pending -> informed -> buy|return
 *      buy      -> bought (terminal, Finance pinged)
 *      return   -> returned -> in_storage|to_loot|to_testing (terminal, Finance pinged)
 *
 *  Phone tariffs:
 *    pending -> informed -> wants_to_keep|wants_to_cancel
 *      wants_to_keep   -> transfer_requested -> transferred (terminal)
 *      wants_to_cancel -> cancel_requested   -> disconnected (terminal)
 */
export const OFFBOARDING_STATUS = Object.freeze({
  pending:            { label: 'Pending',            badge: 'idle'     },
  informed:           { label: 'Asked user',         badge: 'warning'  },

  // Device branch
  buy:                { label: 'Will buy',           badge: 'ready'    },
  return:             { label: 'Will return',        badge: 'ready'    },
  returned:           { label: 'Returned',           badge: 'success'  },
  bought:             { label: 'Bought (paid)',      badge: 'success'  },
  in_storage:         { label: 'In storage',         badge: 'success'  },
  to_loot:            { label: 'Sent to loot',       badge: 'success'  },
  to_testing:         { label: 'Kept for testing',   badge: 'success'  },

  // Phone-tariff branch
  wants_to_keep:      { label: 'Wants to keep',      badge: 'ready'    },
  wants_to_cancel:    { label: 'Wants to cancel',    badge: 'ready'    },
  transfer_requested: { label: 'Transfer requested', badge: 'warning'  },
  cancel_requested:   { label: 'Cancel requested',   badge: 'warning'  },
  transferred:        { label: 'Transferred',        badge: 'success'  },
  disconnected:       { label: 'Disconnected',       badge: 'success'  },

  cancelled:          { label: 'Cancelled',          badge: 'error'    },
});

/** Top-level device status (matches functions/lib/normalise-device.js).
 *  Chip kinds: colour fills only for what needs attention; steady states
 *  (in use, in storage, sold) are outlined, idle ones plain. */
export const DEVICE_STATUSES = Object.freeze({
  in_use:     { label: 'In use',     badge: 'ready'    },
  unassigned: { label: 'Unassigned', badge: 'idle'     },
  in_storage: { label: 'In storage', badge: 'ready'    },
  testing:    { label: 'Testing',    badge: 'idle'     },
  retired:    { label: 'Retired',    badge: 'error'    },
  // On Loot (Send to Loot): a draft listing until Loot hands it over.
  for_sale:   { label: 'For sale',   badge: 'ready'    },
  sold:       { label: 'Sold',       badge: 'ready'    },
  // The person's own laptop, enrolled in an MDM for data security only.
  personal:   { label: 'Personal',   badge: 'idle'     },
  // Disposed in Abra ("Úplné vyřazení") — registry-only. Lives exclusively
  // in the Devices page's Decommissioned tab; excluded from every default
  // list, search, and holder query.
  decommissioned: { label: 'Decommissioned', badge: 'error' },
});

/** High-level employee phase. */
export const PHASES = Object.freeze({
  onboarding: 'onboarding',
  active:     'active',
  offboarding: 'offboarding',
  done:       'done',
});

/**
 * Display labels for well-known context tags from Flexi `stitky`.
 * Unknown tags fall through with default chip styling.
 */
export const CONTEXT_TAG_LABELS = Object.freeze({
  testing: { label: 'Testing' },
  lab:     { label: 'Lab'     },
  storage: { label: 'Storage' },
  spare:   { label: 'Spare'   },
});

export function contextTagInfo(tag) {
  if (!tag) return { label: '' };
  const key = String(tag).trim().toLowerCase();
  return CONTEXT_TAG_LABELS[key] || { label: tag };
}
